import React, { useEffect, useMemo, useState } from 'react';
import { Modal } from 'antd';

import EventFormElement from '../events/EventFormElement';
import RecordBadge, { RecordIdentity } from '../common/RecordBadge';
import getItemDisplayValue from '../../utils/items';
import { missingRequired } from '../../utils/required';
import { normalizeId } from '../../utils/ids';

/**
 * The item's form for what a board move leaves empty (KPR-1395). A drop that
 * would leave a required element blank — `required`, or a `requiredWhen` the
 * new value trips ("acceptance criteria once it is Ready") — opens this
 * instead of sending anything: the same inputs the item form draws
 * (`EventFormElement`), for exactly the missing elements, under the item's
 * identity. **Move** writes the move and these values as one update;
 * **Cancel** sends nothing and the card stays where it was.
 *
 * The check runs again as the person types, over the row as it would stand,
 * so a value that makes another element required shows that one too.
 */
export default function BoardFillModal({ fill, cardsById, getElementsForCard, resolveLookupValue, saving = false, onSubmit, onCancel }) {
	const item = fill?.item || null;
	const card = item ? cardsById?.[normalizeId(item.card_id)] || null : null;
	const defs = useMemo(
		() => (item && typeof getElementsForCard === 'function' ? getElementsForCard(item.card_id) || [] : []),
		[item, getElementsForCard]
	);
	const [values, setValues] = useState({});
	const [tried, setTried] = useState(false);
	useEffect(() => { setValues({}); setTried(false); }, [fill]);

	const merged = useMemo(
		() => ({ ...(item?.elements || {}), ...(fill?.elements || {}), ...values }),
		[item, fill, values]
	);
	// The elements asked for: the ones missing when the dialog opened, plus
	// any a value typed here makes required.
	const asked = useMemo(() => {
		const names = new Set((fill?.missing || []).map(entry => entry.element));
		missingRequired(defs, merged).forEach(entry => names.add(entry.element));
		return defs.filter(def => names.has(def.name));
	}, [fill, defs, merged]);
	const stillMissing = useMemo(() => new Map(missingRequired(defs, merged).map(entry => [entry.element, entry.message])), [defs, merged]);

	if (!fill) return null;
	const title = getItemDisplayValue({ card, elementDefs: defs, item, resolveLookupValue });
	const cardType = card?.label?.singular || card?.name || 'Item';
	const where = fill.label ? `to ${fill.label}` : '';

	const submit = () => {
		setTried(true);
		if (stillMissing.size) return;
		const sent = {};
		asked.forEach((def) => { if (Object.prototype.hasOwnProperty.call(values, def.name)) sent[def.name] = values[def.name]; });
		onSubmit?.(sent);
	};

	return (
		<Modal
			open
			title={`Fill in to move ${where}`.trim()}
			okText="Move"
			onOk={submit}
			confirmLoading={saving}
			onCancel={onCancel}
			destroyOnClose
			className="board-fill"
		>
			<div className="modal-identity">
				<RecordIdentity kicker={cardType} badge={<RecordBadge kind="item" record={card} title={title} />} />
			</div>
			<p className="board-fill__lead">
				{asked.length === 1 ? 'This item needs one more thing first.' : `This item needs ${asked.length} more things first.`}
			</p>
			<div className="board-fill__fields">
				{asked.map(def => (
					<EventFormElement
						key={def.name}
						event={{ ...item, elements: merged }}
						element={def}
						value={merged[def.name]}
						onChange={(name, value) => setValues(prev => ({ ...prev, [name]: value }))}
						cardMeta={card}
						showIconBadge={false}
						errorMessage={tried ? stillMissing.get(def.name) || null : null}
						siblingDefs={defs}
						storedElements={item?.elements || null}
					/>
				))}
			</div>
		</Modal>
	);
}
